/**
 * One reading of a Company-route error for every screen that shows one.
 * The codes a user can act on get an instruction; anything else shows the
 * server's own message.
 *
 * Some refusals arrive with their code on the error (`ConflictError` and
 * friends carry one); others arrive as a generic validation / forbidden
 * error whose MESSAGE starts with the code (`APPROVAL_OWNER_DENY: …`). Both
 * are read here so a screen never has to know which.
 */

const COMPANY_ERROR_TEXT: Record<string, string> = {
  MFA_REQUIRED_FOR_COMPANY:
    'Managing a Company requires two-factor authentication. Enable it in Account Settings, then try again.',
  MFA_REVALIDATION_REQUIRED: 'Your session predates a recent security change. Sign in again.',
  COMPANY_NAME_RESERVED:
    'That name is reserved (Company names cannot claim the Fias brand). Choose another.',
  COMPANY_OWNERS_CHANGED:
    'The Company’s owners changed since this screen was shown. Review the owners named now and accept again.',
  PAYER_STORAGE_QUOTA_EXCEEDED:
    'The Company’s payer does not have room in their storage plan for this workspace. The payer needs a larger plan (or less stored) first.',
  COMPANY_WORKSPACES_LIMIT: 'This Company already links as many workspaces as it can.',
  COMPANY_MEMBER_GUEST:
    'A guest account cannot join a Company. They need to finish signing up first.',
  COMPANY_MEMBER_INVITE_PENDING: 'That person already has an open invitation to this Company.',
  COMPANY_MEMBER_INVITES_TOO_MANY:
    'This Company has too many open invitations. Cancel some, or wait for people to answer.',
  COMPANY_MEMBER_INVITEE_BUSY:
    'That person has too many open Company invitations. They need to answer some first.',
  COMPANY_MEMBERS_LIMIT: 'This Company already has as many people as it can hold.',
  COMPANY_MEMBER_EXISTS: 'That person is already on this Company. Change their role instead.',
  APPROVAL_OWNER_DENY:
    'A workspace owner denied this arche here. Only an owner of this workspace can change that.',
  // ADR-034: a publish changed the app's roles between the look and the approval.
  ACCESS_POLICY_CHANGED:
    'This app’s roles changed since this was shown, so nothing was approved. Review them as they are now, then approve again.',
  // Company-owned arches + audience (ADR-026)
  DISCLOSURE_CHANGED:
    'The payer or the arche’s team changed since this screen was shown. Review it again before accepting.',
  TRANSFER_PENDING: 'This arche already has an open ownership proposal.',
  TRANSFER_EXPIRED: 'This proposal has lapsed. Ask for it to be sent again.',
  TRANSFER_NOT_PENDING: 'This proposal has already been answered.',
  OWNERSHIP_CHANGED:
    'This arche changed owner while you were working on it. Reload the page and try again.',
  PROPOSER_NOT_AFFILIATED:
    'The person who offered this arche no longer belongs to the Company, so it cannot be accepted.',
  COMPANY_ARCHE_LIMIT: 'This Company already owns as many arches as it can. Release one first.',
  COMPANY_OWNS_ARCHES:
    'This Company owns arches. Release them (Company → Owned arches) before deleting the Company.',
  COMPANY_OWNED_ARCHE: 'This arche is owned by a Company.',
  COMPANY_ARCHE_TRANSFER_DISABLED:
    'Moving arches to a Company is paused right now. Open proposals stay open.',
  ARCHE_RESTRICTION_DISABLED: 'Restricting an arche is not available right now.',
  COMPANY_GOVERNED: 'This arche is managed by its Company’s owners and admins.',
  COMPANY_GOVERNANCE_SESSION_ONLY:
    'This arche is managed by its Company, signed in on Fias with two-factor authentication — not with an API key.',
  OWNER_ROLE_COMPANY_GOVERNED:
    'A Company-owned arche has no owner collaborators — its Company governs it. Grant Publish or Read instead.',
  AUDIENCE_GRANT_LIMIT:
    'This arche has as many individual grants as it can hold. Share it with a workspace or the Company instead.',
  GRANT_EXISTS: 'They already have access.',
  GRANTEE_INVALID:
    'That can’t be given access (a workspace must be a shared workspace; guests can’t be added).',
  GRANTEE_NOT_AFFILIATED:
    'You can only give access to a workspace, Company or guild you belong to yourself.',
  AI_ARCHE_CANNOT_BE_PUBLIC:
    'An arche that uses AI needs a signed-in user, so it can’t be open to everyone.',
  RELEASE_TARGET_INVALID: 'That account cannot own an arche.',
  NEW_CONTRIBUTOR_INVALID: 'That account cannot be paid for an arche.',
  ARCHE_NOT_ACTIVE: 'Only an active arche can move to a Company.',
  USERNAME_NOT_FOUND: 'No active user found with that username.',
  USER_NOT_FOUND:
    'No Fias account matches that email or username. Ask them to sign up, then try again.',
  COLLABORATOR_EXISTS: 'They are already on this arche’s team.',
};

/** The Company error code carried by `err`, from its `code` or its message prefix. */
export function companyErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string' && code in COMPANY_ERROR_TEXT) return code;
  const message = err instanceof Error ? err.message : '';
  const prefix = /^([A-Z][A-Z0-9_]+):/.exec(message)?.[1];
  if (prefix && prefix in COMPANY_ERROR_TEXT) return prefix;
  return typeof code === 'string' ? code : null;
}

/**
 * True when the server answered with a 4xx — "not yours", "not found", "not
 * a plugin". A screen that only manages can stay silent on one; anything else
 * (a 5xx, the network) is a failure to load, which it should say.
 */
export function isRequestRefusal(err: unknown): boolean {
  const status = (err as { status?: unknown })?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

export function describeCompanyError(err: unknown, fallback = 'Please try again.'): string {
  const code = companyErrorCode(err);
  if (code === 'COMPANY_NAME_RESERVED' && err instanceof Error) {
    // The server names the rule the name broke; show it after the code.
    const detail = err.message.replace(/^COMPANY_NAME_RESERVED:\s*/, '');
    if (detail && detail !== err.message) return `That name is reserved: ${detail}`;
  }
  if (code && code in COMPANY_ERROR_TEXT) return COMPANY_ERROR_TEXT[code];
  return err instanceof Error ? err.message : fallback;
}
